import { apiClient } from '@/api/client';

export interface SymptomDetailItem {
  symptom: string;
  step: number;
  weighted_score: number;
}

export interface TemporaryRemedyItem {
  remedy: string;
  score: number;
  matches: string[];
  symptom_details?: SymptomDetailItem[];
  AvgSymptomScore?: number;
  NumSymptomScore?: number;
  TotalWeightedScore?: number;
  StdDevSymptomScore?: number;
  TotalPolarWeightedScore?: number;
  P80SymptomScore?: number;
  CoverageQualityScore?: number;
  ConsistencyScore?: number;
  PolarPenalty?: number;
}

const STEP_DEFINITIONS = [
  { stepNum: 2, stepName: 'Step 2: Case Description', shortName: 'P2 Desc' },
  { stepNum: 3, stepName: 'Step 3: Physical Symptoms', shortName: 'P3 Physical' },
  { stepNum: 4, stepName: 'Step 4: Mental Symptoms', shortName: 'P4 Mental' },
  { stepNum: 5, stepName: 'Step 5: Symptom Selection (Intake)', shortName: 'P5 Intake' },
];

function getStepSummaryText(items: SymptomDetailItem[] = []): string {
  if (!items || items.length === 0) return '0 (0.00)';
  const count = items.length;
  const score = items.reduce((acc, curr) => acc + (curr.weighted_score || 0), 0);
  return `${count} (${score.toFixed(2)})`;
}

export async function logTemporaryRemedies(
  stepName: string,
  stepNumber: number,
  patient: {
    email?: string;
    name?: string;
    age?: number | string;
    gender?: string;
    consultationToken?: string;
  },
  consultationDate?: string
): Promise<TemporaryRemedyItem[]> {
  if (!patient?.email?.trim() || !patient?.consultationToken?.trim()) {
    return [];
  }

  const date0 = consultationDate || new Date().toISOString().slice(0, 10);

  try {
    const response = await apiClient.post('/remedies/top', {
      patient: {
        email: patient.email,
        name: patient.name || '',
        age: patient.age,
        gender: patient.gender,
        description_0: [],
        date_0: date0,
        consultation_token: patient.consultationToken,
      },
      top_k: 5,
    });

    const remedies: TemporaryRemedyItem[] =
      response.data?.remedies || (Array.isArray(response.data) ? response.data : []);

    console.group(`🔬 [TEMPORARY REMEDIES RESULT - Step ${stepNumber}: ${stepName}]`);
    console.log(`Consultation date: ${date0} | Patient: ${patient.name || patient.email}`);

    if (!remedies || remedies.length === 0) {
      console.log(
        '⚠️ No remedies meet the minimum threshold at this step (at least 2 quality matching symptoms required).'
      );
    } else {
      console.table(
        remedies.map((r, idx) => {
          const detailsByStep: Record<number, SymptomDetailItem[]> = {};
          (r.symptom_details || []).forEach((item) => {
            const stepKey = Number(item.step) || 0;
            if (!detailsByStep[stepKey]) detailsByStep[stepKey] = [];
            detailsByStep[stepKey].push(item);
          });

          return {
            'Rank': `#${idx + 1}`,
            'Remedy': r.remedy,
            'TotalScore (Formula)': Number(r.score.toFixed(4)),
            'Coverage & Quality [p80*ln(N+1)]': Number((r.CoverageQualityScore ?? 0).toFixed(4)),
            'Consistency Factor [1/(1+StdDev)]': Number((r.ConsistencyScore ?? 0).toFixed(4)),
            'Antagonist Penalty [-0.3*Polar]': Number((r.PolarPenalty ?? 0).toFixed(4)),
            'Matches (N)': r.matches?.length ?? r.NumSymptomScore ?? 0,
            'P2 Desc (# / Score)': getStepSummaryText([...(detailsByStep[2] || []), ...(detailsByStep[0] || [])]),
            'P3 Physical (# / Score)': getStepSummaryText(detailsByStep[3]),
            'P4 Mental (# / Score)': getStepSummaryText(detailsByStep[4]),
            'P5 Intake (# / Score)': getStepSummaryText([...(detailsByStep[5] || []), ...(detailsByStep[1] || [])]),
            'Avg Symptom Score': Number((r.AvgSymptomScore ?? 0).toFixed(4)),
            'Total Weighted': Number((r.TotalWeightedScore ?? 0).toFixed(4)),
          };
        })
      );

      console.groupCollapsed('📋 Detailed breakdown per remedy and STEP CONTRIBUTION');
      remedies.forEach((r, idx) => {
        const cov = r.CoverageQualityScore ?? 0;
        const cons = r.ConsistencyScore ?? 0;
        const pol = r.PolarPenalty ?? 0;
        console.group(`#${idx + 1} Remedy: ${r.remedy} (TotalScore: ${r.score.toFixed(4)})`);
        console.log(
          `Formula: TotalScore = (${cov.toFixed(4)} * ${cons.toFixed(4)}) - ${pol.toFixed(4)} = ${r.score.toFixed(4)}`
        );
        console.log('Global Formula Components:', {
          CoverageQualityScore: r.CoverageQualityScore,
          ConsistencyScore: r.ConsistencyScore,
          PolarPenalty: r.PolarPenalty,
          P80SymptomScore: r.P80SymptomScore,
          AvgSymptomScore: r.AvgSymptomScore,
          StdDevSymptomScore: r.StdDevSymptomScore,
          TotalWeightedScore: r.TotalWeightedScore,
          TotalPolarWeightedScore: r.TotalPolarWeightedScore,
          NumSymptomScore: r.NumSymptomScore,
        });

        // Group symptom details by step
        const detailsByStep: Record<number, SymptomDetailItem[]> = {};
        (r.symptom_details || []).forEach((item) => {
          const stepKey = Number(item.step) || 0;
          if (!detailsByStep[stepKey]) detailsByStep[stepKey] = [];
          detailsByStep[stepKey].push(item);
        });

        const totalW =
          r.TotalWeightedScore && r.TotalWeightedScore > 0
            ? r.TotalWeightedScore
            : (r.symptom_details || []).reduce((acc, curr) => acc + (curr.weighted_score || 0), 0) || 1;

        const stepBreakdownTable = STEP_DEFINITIONS.map(({ stepNum, stepName }) => {
          const items = detailsByStep[stepNum] || [];
          const count = items.length;
          const scoreSum = items.reduce((acc, curr) => acc + (curr.weighted_score || 0), 0);
          const pct = totalW > 0 ? (scoreSum / totalW) * 100 : 0;
          const symptomList = items.map((i) => i.symptom).join(' | ');

          return {
            'Input Step': stepName,
            'Symptoms (#)': count,
            'Weighted Score': Number(scoreSum.toFixed(4)),
            'Contribution (%)': `${pct.toFixed(1)}%`,
            'Symptoms Added in this Step': symptomList || '(none)',
          };
        });

        console.log('📊 Symptom Contribution per Input Step:');
        console.table(stepBreakdownTable);

        console.groupEnd();
      });
      console.groupEnd();
    }
    console.groupEnd();

    return remedies;
  } catch (err) {
    console.error(`[TEMPORARY REMEDY ERROR] Error calculating temporary remedies in Step ${stepNumber}:`, err);
    return [];
  }
}
